import * as React from "react";
import { RadiosEditor } from "../../../../../../../react-core/Html/RadiosEditor";
import { PercentageOrMoney } from "../../../../../../Shared/PercentageOrMoney/PercentageOrMoney";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { SubAccountType } from "../../../../../../../Wrap/Constants/Enums/Domain/SubAccountType";
import { SectionHeader } from "../../../NewBusinessWizard/Components/SharedComponents";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { SourceTypeArray, SourceTypeMap } from "../../Resource/Contents";
import { SourceType } from "../../../../../../../Wrap/Constants/Enums/Domain/SourceType";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { MoneyOrPercentage } from "../../../../../../../Wrap/Models/Models/Shared/MoneyOrPercentage";
import { PensionBase } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/PensionBase";
import { ProductDetailsStore } from "../../../../../Stroes/Variable";
import { calculateNetDrawdownValue, getCrystallisationAmount } from "./Helper/DrawdownDetailHelper";
import { MoneyOrPercentageType } from "../../../../../../../Wrap/Constants/Enums/MoneyOrPercentageType";
import { AdviserChargesValidation } from "../../AdviserCharges/Resource/Contents";

interface IDIACProps extends IProcessComponentProps {
    data: DrawdownModel;
    modelData: PensionBase;
    onChange: (changes) => void;
    existingAccount: boolean;
}

interface IDIACState {
    wrapcashCurrentTotal: number;
}

export class DIAC extends ProcessComponent<IDIACProps, IDIACState> {
    constructor(props: any) {
        super(props);
        this.state = {
            wrapcashCurrentTotal: ProductDetailsStore.getSubAccount(SubAccountType.WrapCash)?.TotalValue?.Amount,
        };
        this.subscribeToStore(ProductDetailsStore, () => this.onProductDetailsChange());
    }

    private onProductDetailsChange() {
        const { existingAccount } = this.props;
        if (existingAccount && !this.state.wrapcashCurrentTotal) {
            const result = ProductDetailsStore.getSubAccount(SubAccountType.WrapCash);
            if (result) {
                this.setState({
                    wrapcashCurrentTotal: result.TotalValue?.Amount,
                });
            }
        }
    }

    componentDidMount() {
        super.componentDidMount();
        if (!this.props.data?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.SourceType) {
            const charge = {
                ...this.props.data?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge,
                SourceType: SourceType.ProductCash,
            };
            this.onDataChange(
                this.props.onChange,
                { DrawdownInitialAdviserCharge: charge },
                this.props.data,
                "DrawdownAdviserCharge"
            );
        }
    }

    iacGiven(): boolean {
        const amount = this.props.data?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Money;
        const percentage = this.props.data?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge?.Percentage;
        return (percentage != null && percentage > 0) || (amount && amount.Amount != null && amount.Amount > 0);
    }

    validateSufficientProductCashForDIAC(total: number) {
        const { data, existingAccount } = this.props;
        const diac = data?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge;
        if (!diac?.SourceType || diac?.SourceType == SourceType.ProductCash) {
            return true;
        }
        if (existingAccount) {
            const v = (diac?.Type == MoneyOrPercentageType.Money ? diac?.Money?.Amount : diac?.Percentage * total / 100) || 0;
            return v <= this.state.wrapcashCurrentTotal;
        } else {
            return true;
        }
    }


    renderAlert() {
        const percentage = this.getPercentageBasedOnAmountOrPercentage();
        
        const alerts = [];

        if (percentage >= 10 && percentage < 100) {
            alerts.push(
                <Alert
                    alertType={AlertType.Warning}
                    message={
                        `You have requested an adviser payment equivalent to 
                        ${percentage.toFixed(2)}%. Please continue if this is correct.`
                    }
                />
            );
        }
        
        if (alerts.length > 0) {
            return <>{alerts}</>;
        }
    }

    getPercentageBasedOnAmountOrPercentage(): number {
        const diac = this.props.data?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge;
        let percentageValue = diac?.Percentage;
        if (diac && diac.Money && diac.Money.Amount > 0) {
            const total = this.props.data?.CrystallisationDetails.CrystallisationAmount.Amount;
            percentageValue = total ? (diac.Money.Amount / total) * 100 : 0;
        }

        return percentageValue ? percentageValue : 0;
    }



    public render(): JSX.Element {
        const { data, onChange, modelData } = this.props;
        const netDrawdownValue = calculateNetDrawdownValue(data, modelData?.PensionProtection) * 0.9999;
        const roundedNetDrawdownValue = Math.round(netDrawdownValue * 100) / 100;
        const totalforEstimation = getCrystallisationAmount(data);

        const adviserCharge: MoneyOrPercentage = data?.DrawdownAdviserCharge?.DrawdownInitialAdviserCharge;
        const atLeast = { errorMessage: `Adviser charge must be greater than or equal to zero`, value: 0 };
        const atMost = { errorMessage: `cannot exceed the net drawdown value £` + roundedNetDrawdownValue, value: roundedNetDrawdownValue};
        return (
            <>
                {SectionHeader("Drawdown initial adviser charge", "If a percentage is selected, the amount will be based on the total being moved to drawdown. The drawdown initial adviser charge will be deducted from the drawdown pot after any tax free cash has been paid. ")}
                {adviserCharge?.SourceType === SourceType.WrapCash && this.iacGiven() && (
                    <Alert alertType={AlertType.Warning} message={AdviserChargesValidation.WrapCashDIACWarningMessage} />
                )}
                {this.renderAlert()}
                <PercentageOrMoney
                    label="Rate"
                    data={adviserCharge}
                    onChange={(v) => {
                        const charge = { ...adviserCharge, ...v };
                        if (!charge.SourceType || charge.SourceType == SourceType.NotSpecified) {
                            charge.SourceType = SourceType.ProductCash
                        }
                        this.onDataChange(
                            onChange,
                            { DrawdownInitialAdviserCharge: charge },
                            data,
                            "DrawdownAdviserCharge"
                        );
                    }}
                    onRef={(ref) => this.appendChild(ref)}
                    atLeast={atLeast}
                    atMost={atMost}
                    totalForEstimation={totalforEstimation}
                    cssPercentageOrMoneyClasses="control-size-100 mb-36"
                    validationRules={[
                        {
                            errorMessage:
                                "There is insufficient cash to pay the Drawdown Initial Adviser Charge(s) requested",
                            validator: (v) => {
                                return true;
                            },
                        },
                    ]}
                />
                <RadiosEditor
                    cssWrapper="rg-inline mb-16"
                    label="Take the charge from"
                    selected={adviserCharge?.SourceType??SourceType.ProductCash} 
                    options={SourceTypeArray}
                    getText={(v) => {
                        return SourceTypeMap[v];
                    }}
                    onChange={(v) => {
                        const charge = { ...adviserCharge, SourceType: v };
                        this.onDataChange(
                            onChange,
                            { DrawdownInitialAdviserCharge: charge },
                            data,
                            "DrawdownAdviserCharge"
                        );
                    }}
                    required="Please choose "
                />
                {!this.validateSufficientProductCashForDIAC(totalforEstimation) && (
                    <Alert
                        alertType={AlertType.Warning}
                        message="There is insufficient cash to pay the Drawdown Initial Adviser Charge(s) requested"
                    />
                )}                
            </>
        );
    }
}
